import * as React from "react";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";
import { CombinedFilmObject } from "../types/dataTypes.ts";
import { useLocation, useParams } from "react-router-dom";
import { fetchScreeningsForIndividualPopularFilm } from "../helpers/onCallFunctions.ts";
import { TitleElement } from "../components/TitleElement/TitleElement.tsx";
import { LandingPageDescriptionBoxes } from "../components/LandingPageDescriptionBoxes/LandingPageDescriptionBoxes.tsx";
import { TextLinkTemplate } from "../components/TextLinks/TextLinkTemplate.tsx";
import { LinkToCinemaSelectPage } from "../components/TextLinks/LinkToCinemaSelectPage.tsx";
import {
  capitaliseFirstLetterOfString,
  createListOfAllVenuesForIndividualPopularFilmPage,
  normaliseUpperCaseFilmTitle,
} from "../helpers/stringHelpers.ts";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";
import { FilmTile } from "../components/FilmTile/FilmTile.tsx";
import { NotFoundPage } from "./NotFoundPage.tsx";
import { Box } from "@mui/material";

export const IndividualPopularFilmPage = () => {
  const [numberOfScreenings, setNumberOfScreenings] = React.useState(0);
  const [film, setFilm] = React.useState<CombinedFilmObject | null>(null);
  const [displayFilmTitleWithLowerCase, setDisplayFilmTitleWithLowerCase] =
    React.useState<string>("");
  const [filmNotFound, setFilmNotFound] = React.useState(false);
  const [venuesIncludedText, setVenuesIncludedText] = React.useState("");
  const [numberOfUniqueVenues, setNumberOfUniqueVenues] =
    React.useState<number>();

  const [isLoading, setIsLoading] = React.useState(true);

  const location = useLocation();

  const { citySlug, filmSlug } = useParams() as {
    citySlug: string;
    filmSlug: string;
  };

  const capitalisedCityName = capitaliseFirstLetterOfString(citySlug);

  const getNumberOfScreenings = (film: CombinedFilmObject): number => {
    return film.screenings_data.reduce((total, day) => {
      return total + day.screenings.length;
    }, 0);
  };

  const getNumberOfUniqueVenues = (film: CombinedFilmObject): number => {
    const venues = new Set<string>();

    for (const day of film.screenings_data) {
      for (const screening of day.screenings) {
        venues.add(screening.venue_name);
      }
    }

    return venues.size;
  };

  React.useEffect(() => {
    const fetchScreeningsData = async () => {
      try {
        const film = await fetchScreeningsForIndividualPopularFilm(
          citySlug,
          filmSlug,
        );
        if (film) {
          const screeningsLength = getNumberOfScreenings(film);
          if (screeningsLength > 0) {
            setNumberOfScreenings(screeningsLength);
            const normalisedTitle = normaliseUpperCaseFilmTitle(
              film.display_film_title,
            );
            setDisplayFilmTitleWithLowerCase(normalisedTitle);
            setVenuesIncludedText(
              createListOfAllVenuesForIndividualPopularFilmPage(
                normalisedTitle,
                film,
              ),
            );
            setNumberOfUniqueVenues(getNumberOfUniqueVenues(film));
            setFilm(film);
          } else {
            setFilmNotFound(true);
          }
        } else {
          setFilmNotFound(true);
        }
      } catch (error) {
        setFilmNotFound(true);
        console.error("Error fetching screenings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScreeningsData();
  }, [citySlug, filmSlug]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  if (!isLoading && filmNotFound) {
    return (
      <NotFoundPage
        title="Film not found | CinemaGuide"
        description="This film page either doesn’t exist or has been removed."
      />
    );
  }

  if (isLoading || !film) {
    return <LoadingElementInBox />;
  }

  return (
    <>
      <StandardPageHead
        title={`${displayFilmTitleWithLowerCase} Showtimes in ${capitalisedCityName} - All Cinemas`}
        description={`Browse all ${numberOfScreenings} showings of ${displayFilmTitleWithLowerCase} in ${capitalisedCityName}`}
        pageUrl={`https://cinemaguideireland.com/popular-films/${citySlug}/${filmSlug}`}
      />
      <TitleElement
        headerOneText={`Watch ${displayFilmTitleWithLowerCase} in ${capitalisedCityName}.`}
        headerTwoText={`Browse all ${capitalisedCityName} screenings of ${displayFilmTitleWithLowerCase} in one place.`}
      />
      <LandingPageDescriptionBoxes
        locationText={capitalisedCityName}
        boxOneHeader={`Showtimes for ${numberOfUniqueVenues}+ ${capitalisedCityName} Cinemas`}
        boxOneText={venuesIncludedText}
        boxTwoText={`Easily buy tickets for any showing of ${displayFilmTitleWithLowerCase} in ${capitalisedCityName}. Pay no extra fees. Book tickets fast.`}
        boxThreeHeader={`${numberOfScreenings}+ Showtimes in ${capitalisedCityName}`}
        boxThreeText={`Browse over ${numberOfScreenings} screenings of ${displayFilmTitleWithLowerCase} in ${capitalisedCityName} on one page. Compare different showtimes. Get the cheapest cinema tickets.`}
      />

      <Box sx={{ my: 6 }}>
        <TextLinkTemplate
          leadText={`Find every other film showing in ${capitalisedCityName}`}
          linkText={"here"}
          href={citySlug}
          appendHref={false}
          fontSizeParam={{ xs: "small", md: "x-large" }}
        />
        <LinkToCinemaSelectPage
          leadText={`Not in ${capitalisedCityName}? `}
          appendHref={false}
          fontSizeParam={{ xs: "small", md: "x-large" }}
        />
      </Box>

      <Box
        sx={{
          width: {
            xs: "90%",
            md: "65%",
          },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          margin: "0px auto",
          mt: 2,
        }}
      >
        <FilmTile
          key={`${film.title}_ALL_DATES`}
          title={film.display_film_title}
          description={film.description ?? ""}
          image_link={film.image_link}
          screenings={film.screenings_data}
          rating={film.rating}
          lengthInMinutes={film.length_in_minutes}
          tabDateLabel={"ALL_DATES"}
          lazy={false}
          optimise={false}
          forPopularFilmPage={true}
        />
      </Box>
    </>
  );
};
